<template>
  <div class="spform" role="presentation" @keydown.esc.prevent="onEscape" @keydown="onHotkey">
    <div class="spform__backdrop" @click="onBackdrop" />
    <div
      ref="dialogEl"
      class="spform__dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="spform__head">
        <div class="spform__head-top">
          <div class="spform__title-row">
            <h2 :id="titleId">{{ isEdit ? '상품 수정' : '상품 등록' }}</h2>
            <div class="spform__meta-badges">
              <span v-if="isEdit && productCode" class="spform__code-badge">
                <AppIcon icon="mdi-barcode" :size="14" />
                {{ productCode }}
              </span>
              <span v-if="isEdit && currentStatus" class="sui-badge" :class="`is-${currentStatus.toLowerCase()}`">
                {{ MALL_PRODUCT_STATUS_LABEL[currentStatus] || currentStatus }}
              </span>
            </div>
          </div>

          <div class="spform__head-actions">
            <button type="button" class="spform__close" aria-label="닫기" :disabled="saving" @click="onClose">
              <AppIcon icon="mdi-close" :size="20" />
            </button>
          </div>
        </div>
      </header>

      <div v-if="loading" class="spform__loading">
        <AppIcon icon="mdi-loading" :size="28" />
        <p>상품을 불러오는 중입니다…</p>
      </div>

      <template v-else>
        <div class="spform__body spform__body--register" :class="{ 'spform__body--sale': form.mallKind === 'SALE' }">
          <div class="spform__main">
            <section v-if="rejectReason" class="spform__alert spform__alert--warn">
              <AppIcon icon="mdi-alert-circle-outline" :size="20" />
              <div>
                <strong>검수에서 반려된 상품입니다.</strong>
                <p>{{ rejectReason }}</p>
              </div>
            </section>

            <section v-if="needsReReview" class="spform__alert spform__alert--info">
              <AppIcon icon="mdi-check-decagram-outline" :size="20" />
              <div>
                <strong>이 상품은 자동 승인으로 저장됩니다.</strong>
                <p>
                  이름·설명·이미지·옵션·주문 단위·배송 등은 저장 즉시 몰에 반영되고 판매 상태(판매중·품절·숨김)는 그대로 유지됩니다.
                  판매가·공급가·마진 배분율을 바꾸면 그때만 관리자 재검수로 넘어가고 검수 전까지 몰에서 잠깐 내려갑니다.
                </p>
              </div>
            </section>

            <section v-if="localDraftBanner" class="spform__alert">
              <div>
                <strong>브라우저에 임시 보관된 작성 내용이 있습니다.</strong>
                <p>{{ localDraftBanner }}</p>
              </div>
              <div class="spform__draft-actions">
                <button type="button" class="sui-btn sui-btn--sm sui-btn--primary" @click="restoreLocalDraft">
                  이어쓰기
                </button>
                <button type="button" class="sui-btn sui-btn--sm" @click="discardLocalDraft">버리기</button>
              </div>
            </section>

            <ProductRegisterSheet
              :form="form"
              tone="seller"
              :store-name="seller?.storeName || ''"
              :categories="[...SELLER_CATEGORIES]"
              :uploader="uploadProductImages"
              :store-shipping="storeShipping"
              :stock-locked="needsReReview"
              :sale-status="currentStatus"
              :can-edit-status="canToggleSaleStatus"
              :is-new="!isEdit"
              @notify="notify"
              @open-stock-history="stockHistoryOpen = true"
              @update-status="onSheetStatus"
            />
          </div>

        </div>


        <footer class="spform__foot" data-tour="seller-product-save">
          <button
            type="button"
            class="sui-btn sui-btn--ghost spform__foot-cancel"
            :disabled="saving"
            @click="onClose"
          >
            취소
          </button>

          <div class="spform__foot-end">
            <p
              v-if="firstProblem"
              class="spform__problem"
              role="button"
              tabindex="0"
              @click="onProblemJump"
              @keydown.enter.prevent="onProblemJump"
            >
              <AppIcon icon="mdi-alert-outline" :size="15" />
              {{ firstProblem }}
            </p>
            <div class="spform__actions">
              <p v-if="needsReReview" class="spform__save-note" :class="{ 'is-warn': hasMarginChange }">
                <AppIcon
                  :icon="hasMarginChange ? 'mdi-shield-alert-outline' : 'mdi-check-decagram-outline'"
                  :size="14"
                />
                <span v-if="hasMarginChange">
                  판매가·공급가·마진이 바뀌었습니다. 저장하면 관리자 재검수로 넘어갑니다.
                </span>
                <span v-else>
                  자동 승인 · 저장 즉시 몰에 반영됩니다.
                </span>
              </p>
              <button
                v-if="!needsReReview"
                type="button"
                class="sui-btn"
                :disabled="saving"
                @click="save('DRAFT')"
              >
                <AppIcon icon="mdi-content-save-outline" :size="16" />
                <span>{{ saving ? '저장 중…' : '임시 저장' }}</span>
              </button>
              <button
                type="button"
                class="sui-btn sui-btn--primary"
                :disabled="saving"
                @click="save('REQUESTED')"
              >
                <AppIcon :icon="saving ? 'mdi-loading' : 'mdi-send-check-outline'" :size="16" />
                <span>
                  {{
                    saving
                      ? '저장 중…'
                      : needsReReview
                        ? hasMarginChange
                          ? '재검수 요청'
                          : '자동 승인 저장'
                        : currentStatus === 'REJECTED'
                          ? '검수 재요청'
                          : isEdit
                            ? '검수 요청'
                            : '등록 요청'
                  }}
                </span>
              </button>
            </div>
          </div>
        </footer>
      </template>
    </div>

    <SellerProductPreview
      v-if="showPreview"
      :product="previewProduct"
      :active-zone="previewZone"
      @close="showPreview = false"
      @edit-zone="onCardEditZone"
    />

    <ProductStockHistoryModal
      v-if="stockHistoryOpen && productCode"
      :product-code="productCode"
      :product-name="form.name"
      audience="seller"
      @close="stockHistoryOpen = false"
      @updated="onStockLedgerUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import ProductRegisterSheet from '@/components/manage/ProductRegisterSheet.vue'
import ProductStockHistoryModal from '@/components/manage/ProductStockHistoryModal.vue'
import SellerProductPreview from '@/components/seller/SellerProductPreview.vue'
import { useSellerData } from '@/composables/useSellerData'
import { useSellerAuth } from '@/composables/useSellerAuth'
import { useSellerProducts } from '@/composables/useSellerProducts'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { SELLER_CATEGORIES } from '@/constants/sellerData'
import {
  PRODUCT_EDIT_SURFACES,
  REQUEST_NOTE_TEMPLATES,
  applyRecentProductDefaults,
  clearLocalProductDraft,
  createProductForm,
  ensureSellDefaults,
  focusTargetFromPreviewZone,
  hydrateProductForm,
  loadLocalProductDraft,
  loadRecentProductDefaults,
  productChecklist,
  productProblems,
  saveLocalProductDraft,
  saveRecentProductDefaults,
  sectionDoneMap,
  surfaceDef,
  surfaceFromPreviewZone,
  surfaceFromSection,
  previewModeForZone,
  previewZoneVisibleOn,
  toPreviewProduct,
  toProductPayload,
  type ProductEditSurface,
  type ProductEditorForm,
  type ProductPreviewMode,
  type ProductPreviewZone,
} from '@/constants/productEditor'
import {
  MALL_PRODUCT_STATUS_LABEL,
  createSellerProduct,
  fetchSellerProduct,
  fetchSellerProducts,
  updateSellerProduct,
  uploadProductImages,
  type MallProductStatus,
  type ServerMallProduct,
} from '@/api/mallProduct'
import { confirmAction } from '@/utils/confirmAction'
import { useSameDayCutoffText } from '@/composables/useContentSnippets'
import { formatShippingChargeLabel } from '@/utils/mallSell'
import { scrollWhenReady } from '@/utils/scrollIntoContainer'

const props = withDefaults(
  defineProps<{
    /** 비어 있으면 신규 등록 */
    productCode?: string
    /** 바깥 클릭·Esc로는 닫지 않는다. 닫기는 X·취소만 받는다. */
    persistent?: boolean
    /** 신규 등록이 들어갈 몰. 수정은 저장된 상품 값을 따른다. */
    initialMallKind?: 'REGULAR' | 'SALE'
  }>(),
  { persistent: false, initialMallKind: 'REGULAR' },
)

const emit = defineEmits<{
  close: []
  saved: [code: string]
}>()

const { notify } = useSellerData()
const { policy, isApproved, seller, saveProfile } = useSellerAuth()
const { upsert, changeStatus } = useSellerProducts()

const DEFAULT_RETURN_POLICY =
  '당일 출고 마감 · 교환 · 반품 기준은 판매업체가 안내한 정책을 따릅니다.\n상품 하자 · 오배송 시 수령 후 판매업체 안내에 따라 교환 · 반품이 가능합니다.\n단순 변심 반품은 판매업체가 안내한 반품 배송비가 부과될 수 있습니다.'

const storeShipping = reactive({
  returnFee: seller.value?.returnFee ?? 3000,
  cutoff: seller.value?.cutoff || '14:00',
  returnPolicy: seller.value?.returnPolicy || DEFAULT_RETURN_POLICY,
})

watch(
  () => seller.value,
  (next) => {
    if (!next) return
    storeShipping.returnFee = next.returnFee ?? 3000
    storeShipping.cutoff = next.cutoff || '14:00'
    storeShipping.returnPolicy = next.returnPolicy || DEFAULT_RETURN_POLICY
  },
)

const titleId = `spform-title-${Math.random().toString(36).slice(2, 7)}`
const dialogEl = ref<HTMLElement | null>(null)
const editorRef = ref<{
  focusFirstIncomplete: () => void
  focusSection: (section: string, fieldSuffix?: string) => void
  focusByZone: (zone: ProductPreviewZone | '') => void
} | null>(null)
const productCode = computed(() => (props.productCode || '').trim())
const isEdit = computed(() => Boolean(productCode.value))
const localDraftBanner = ref('')
let autosaveTimer: ReturnType<typeof setTimeout> | undefined
let autosaveToastAt = 0

const form = reactive(
  createProductForm({
    category: SELLER_CATEGORIES[0],
    shippingFee: policy.value.shippingFee,
    freeShipOver: 0,
  }),
) as ProductEditorForm
const loading = ref(false)
const saving = ref(false)
const showPreview = ref(false)
const stockHistoryOpen = ref(false)
const workMode = ref<'wizard' | 'editor'>('wizard')
const wizardSeed = ref('new')
const wizardRef = ref<{ resetFromForm: () => void } | null>(null)
const editSurface = ref<ProductEditSurface>('product')
const previewMode = ref<ProductPreviewMode>('card')
const previewZone = ref<ProductPreviewZone | ''>('')
const previewTabPulse = ref<ProductPreviewMode | ''>('')
let previewTabPulseTimer: ReturnType<typeof setTimeout> | undefined

function pulsePreviewTab(mode: ProductPreviewMode) {
  previewTabPulse.value = mode
  window.clearTimeout(previewTabPulseTimer)
  previewTabPulseTimer = window.setTimeout(() => {
    previewTabPulse.value = ''
  }, 1200)
}

const currentSurfaceDef = computed(() => surfaceDef(editSurface.value))
const surfaceDoneMap = computed(() => {
  const done = sectionDoneMap(form)
  const map = {} as Record<ProductEditSurface, boolean>
  for (const item of PRODUCT_EDIT_SURFACES) {
    map[item.id] = item.sections.every((id) => done[id])
  }
  return map
})

function refreshWizard(asReview = isEdit.value) {
  workMode.value = 'wizard'
  wizardSeed.value = `${productCode.value || 'new'}-${Date.now()}`
  void nextTick(() => wizardRef.value?.resetFromForm(asReview))
}

function setWorkMode(mode: 'wizard' | 'editor') {
  workMode.value = mode
  if (mode === 'editor') {
    editSurface.value = 'product'
    return
  }
  wizardRef.value?.resetFromForm(isEdit.value || form.name.trim().length >= 2)
}

function goSurface(surface: ProductEditSurface) {
  workMode.value = 'editor'
  const changed = editSurface.value !== surface
  editSurface.value = surface
  previewZone.value = ''
  if (surface === 'product' && previewMode.value !== 'card' && previewMode.value !== 'detail') {
    previewMode.value = 'card'
  }
  const main = dialogEl.value?.querySelector('.spform__main') as HTMLElement | null
  const side = dialogEl.value?.querySelector('.spform__side') as HTMLElement | null
  main?.scrollTo({ top: 0, behavior: 'auto' })
  side?.scrollTo({ top: 0, behavior: 'auto' })
  if (!changed) return
  const first = surfaceDef(surface).sections[0]
  if (!first) return
  void scrollWhenReady(
    () => document.getElementById(first),
    { behavior: 'auto', block: 'start', offsetTop: 16, container: main },
  )
}

function onSurfaceChange(surface: ProductEditSurface) {
  editSurface.value = surface
}

function onPreviewModeChange(mode: ProductPreviewMode) {
  if (editSurface.value === 'product') setPreviewMode(mode)
}

function setPreviewMode(mode: ProductPreviewMode) {
  previewMode.value = mode
  previewZone.value = previewZoneVisibleOn(previewZone.value, editSurface.value, mode)
}

function applyPreviewHighlight(zone: ProductPreviewZone | '') {
  const modeHint = previewModeForZone(zone)
  if (modeHint && editSurface.value === 'product' && previewMode.value !== modeHint) {
    previewMode.value = modeHint
    pulsePreviewTab(modeHint)
  } else if (modeHint && editSurface.value === 'product') {
    previewMode.value = modeHint
  }
  /* 모드를 먼저 맞춘 뒤 존 필터 — 카드에 없는 옵션 등이 상세로 넘어가게 */
  const visible = previewZoneVisibleOn(zone, editSurface.value, previewMode.value)
  previewZone.value = visible
  if (!visible) return

  const side = dialogEl.value?.querySelector('.spform__side') as HTMLElement | null
  if (!side) return

  if (editSurface.value === 'shipping') {
    void scrollWhenReady(
      () => side.querySelector('.spform__ship-preview') as HTMLElement | null,
      { behavior: 'auto', block: 'center', offsetTop: 120, offsetBottom: 24, container: side },
    )
    return
  }

  void scrollWhenReady(
    () => {
      const zoned = side.querySelector(`[data-zone="${visible}"]`) as HTMLElement | null
      if (zoned) return zoned
      return side.querySelector(
        previewMode.value === 'detail' ? '.spform__detail-embed' : '.spform__mall-card',
      ) as HTMLElement | null
    },
    {
      behavior: 'auto',
      block: 'center',
      offsetTop: 130,
      offsetBottom: 24,
      container: side,
    },
    480,
  )
}

function onPreviewZone(zone: ProductPreviewZone | '') {
  applyPreviewHighlight(zone)
}

function onCardEditZone(zone: ProductPreviewZone, fieldSuffix?: string) {
  const next = surfaceFromPreviewZone(zone)
  if (next) editSurface.value = next
  applyPreviewHighlight(zone)
  if (fieldSuffix) {
    const target = focusTargetFromPreviewZone(zone)
    if (target) editorRef.value?.focusSection(target.section, fieldSuffix)
    else editorRef.value?.focusByZone(zone)
  } else {
    editorRef.value?.focusByZone(zone)
  }
}
const currentStatus = ref<MallProductStatus | ''>('')
const rejectReason = ref('')
/** 판매중·품절·숨김이면 내용 수정 시 재검수 필수 */
const needsReReview = computed(() =>
  ['SELLING', 'SOLDOUT', 'HIDDEN'].includes(String(currentStatus.value || '')),
)

function onStockLedgerUpdated(payload: { code: string; stock: number; status?: string }) {
  form.stock = payload.stock
  if (payload.status) currentStatus.value = payload.status as MallProductStatus
  notify(`재고 ${payload.stock.toLocaleString('ko-KR')}개로 반영했습니다.`)
}

const initialFormSnapshot = ref('')
const initialMargin = ref({ price: 0, cost: 0, marginShare: 0 })
const hasMarginChange = computed(
  () =>
    Number(form.price || 0) !== Number(initialMargin.value.price) ||
    Number(form.cost || 0) !== Number(initialMargin.value.cost) ||
    Number(form.marginShare || 0) !== Number(initialMargin.value.marginShare),
)
const requestTemplates = REQUEST_NOTE_TEMPLATES
const hasRecentDefaults = ref(Boolean(loadRecentProductDefaults()))
const copyCandidates = ref<Array<{ code: string; name: string }>>([])
const copySourceCode = ref('')

function formSnapshot(): string {
  return JSON.stringify(form)
}

function markFormClean() {
  initialFormSnapshot.value = formSnapshot()
  initialMargin.value = {
    price: Number(form.price || 0),
    cost: Number(form.cost || 0),
    marginShare: Number(form.marginShare || 0),
  }
}

const dirty = computed(() => initialFormSnapshot.value !== formSnapshot())

function resetForm() {
  const next = createProductForm({
    category: SELLER_CATEGORIES[0],
    shippingFee: policy.value.shippingFee,
    freeShipOver: 0,
  })
  Object.assign(form, next)
  form.mallKind = props.initialMallKind === 'SALE' ? 'SALE' : 'REGULAR'
  currentStatus.value = ''
  rejectReason.value = ''
  editSurface.value = 'product'
  workMode.value = 'wizard'
  wizardSeed.value = `new-${Date.now()}`
  markFormClean()
}

async function loadCopyCandidates() {
  try {
    const list = await fetchSellerProducts()
    copyCandidates.value = list
      .filter((row) => row.code !== productCode.value)
      .slice(0, 40)
      .map((row) => ({ code: row.code, name: row.name }))
  } catch {
    copyCandidates.value = []
  }
}

function offerLocalDraft(code: string) {
  const draft = loadLocalProductDraft()
  if (!draft) {
    localDraftBanner.value = ''
    return
  }
  if ((draft.productCode || '') !== (code || '')) {
    localDraftBanner.value = ''
    return
  }
  const when = new Date(draft.savedAt)
  const label = Number.isNaN(when.getTime())
    ? '이전에'
    : `${when.getMonth() + 1}/${when.getDate()} ${String(when.getHours()).padStart(2, '0')}:${String(
        when.getMinutes(),
      ).padStart(2, '0')}`
  localDraftBanner.value = `${label}에 자동 보관된 내용입니다.`
}

function restoreLocalDraft() {
  const draft = loadLocalProductDraft()
  if (!draft?.form) return
  Object.assign(form, draft.form)
  ensureSellDefaults(form)
  localDraftBanner.value = ''
  refreshWizard(true)
  notify('임시 보관 내용을 불러왔습니다.')
}

function discardLocalDraft() {
  clearLocalProductDraft()
  localDraftBanner.value = ''
  notify('임시 보관을 삭제했습니다.')
}

function scheduleLocalAutosave() {
  window.clearTimeout(autosaveTimer)
  autosaveTimer = window.setTimeout(() => {
    if (!dirty.value || saving.value || loading.value) return
    /* 검수 대기 중이거나 신규·임시저장만 브라우저에 보관 */
    if (currentStatus.value && currentStatus.value !== 'DRAFT' && currentStatus.value !== 'REJECTED') {
      return
    }
    saveLocalProductDraft(form, productCode.value)
    const now = Date.now()
    if (now - autosaveToastAt > 20000) {
      autosaveToastAt = now
      notify('브라우저에 임시 보관했습니다.')
    }
  }, 1600)
}

async function load() {
  if (!productCode.value) {
    resetForm()
    hasRecentDefaults.value = Boolean(loadRecentProductDefaults())
    offerLocalDraft('')
    void loadCopyCandidates()
    return
  }

  loading.value = true
  try {
    const product = await fetchSellerProduct(productCode.value)
    hydrateProductForm(form, product)
    currentStatus.value = product.status
    rejectReason.value = product.status === 'REJECTED' ? product.rejectReason : ''
    markFormClean()
    offerLocalDraft(productCode.value)
    refreshWizard(true)
  } catch (error) {
    notify(error instanceof Error ? error.message : '상품을 불러오지 못했습니다.')
    emit('close')
  } finally {
    loading.value = false
  }
}

function onHotkey(event: KeyboardEvent) {
  if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 's') return
  event.preventDefault()
  if (saving.value || loading.value) return
  /* 판매중 상품은 임시저장 없이 재검수 요청으로만 저장 */
  void save(needsReReview.value ? 'REQUESTED' : 'DRAFT', { skipConfirm: true })
}

function applyRequestTemplate(tpl: string) {
  const next = form.requestNote.trim() ? `${form.requestNote.trim()}\n${tpl}` : tpl
  form.requestNote = next.slice(0, 500)
}

function loadRecentDefaults() {
  const recent = loadRecentProductDefaults()
  if (!recent) {
    notify('불러올 최근 등록값이 없습니다.')
    return
  }
  applyRecentProductDefaults(form, recent)
  refreshWizard(true)
  notify('최근 등록값을 불러왔습니다. 상품명·가격·이미지는 새로 입력하세요.')
}

async function copyFromProduct() {
  const code = copySourceCode.value
  if (!code) return
  try {
    const product = await fetchSellerProduct(code)
    applyProductAsTemplate(product)
    notify(`「${product.name}」값을 폼에 채웠습니다. 상품명을 확인하고 저장하세요.`)
  } catch (error) {
    notify(error instanceof Error ? error.message : '상품을 불러오지 못했습니다.')
  } finally {
    copySourceCode.value = ''
  }
}

function applyProductAsTemplate(product: ServerMallProduct) {
  hydrateProductForm(form, product)
  form.name = ''
  form.requestNote = ''
  form.images = [...(product.images || [])]
  currentStatus.value = ''
  rejectReason.value = ''
  refreshWizard(true)
}

watch(productCode, () => {
  load()
})

watch(
  () => formSnapshot(),
  () => {
    if (loading.value) return
    scheduleLocalAutosave()
  },
)

onMounted(() => {
  document.body.style.overflow = 'hidden'
  load()
  void loadSameDayCutoffSnippet()
  requestAnimationFrame(() => dialogEl.value?.focus())
})

onUnmounted(() => {
  document.body.style.overflow = ''
  window.clearTimeout(autosaveTimer)
})

const checklist = computed(() => productChecklist(form))
const checklistOk = computed(() => checklist.value.filter((rule) => rule.ok).length)
const checklistPct = computed(() =>
  checklist.value.length ? Math.round((checklistOk.value / checklist.value.length) * 100) : 0,
)
const shippingFeeLabel = computed(() => formatShippingChargeLabel(form))
const returnFeeLabel = computed(() => {
  const fee = Math.max(0, Number(storeShipping.returnFee) || 0)
  return fee > 0 ? `${fee.toLocaleString('ko-KR')}원` : '무료'
})
const { sameDayCutoffText, loadSameDayCutoffSnippet } = useSameDayCutoffText(
  () => storeShipping.cutoff,
)
const policyLines = computed(() =>
  String(storeShipping.returnPolicy || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean),
)
const firstFailedCheck = computed(() => checklist.value.find((rule) => !rule.ok) || null)

function revealEditor() {
  workMode.value = 'editor'
}

function scrollToCheck(section: string) {
  revealEditor()
  const next = surfaceFromSection(section)
  if (next) editSurface.value = next
  void nextTick(() => editorRef.value?.focusSection(section))
}

function onProblemJump() {
  revealEditor()
  void nextTick(() => editorRef.value?.focusFirstIncomplete())
}

const statusLabel = computed(() =>
  currentStatus.value ? MALL_PRODUCT_STATUS_LABEL[currentStatus.value] : '',
)

const canToggleSaleStatus = computed(() => {
  const status = currentStatus.value
  return status === 'SELLING' || status === 'SOLDOUT' || status === 'HIDDEN'
})

function onSheetStatus(status: 'SELLING' | 'SOLDOUT' | 'HIDDEN') {
  const target = { value: status }
  void onSaleStatusChange({ target } as unknown as Event)
}

async function onSaleStatusChange(event: Event) {
  const next = (event.target as HTMLSelectElement).value as MallProductStatus
  const prev = currentStatus.value
  if (!productCode.value || !prev || next === prev || saving.value) return
  const ok = await confirmDialog({
    title: '판매 상태 변경',
    message: `판매 상태를 '${MALL_PRODUCT_STATUS_LABEL[next]}'으로 바꿀까요?`,
    confirmLabel: '변경',
    tone: 'primary',
  })
  if (!ok) {
    ;(event.target as HTMLSelectElement).value = prev
    return
  }
  saving.value = true
  try {
    await changeStatus(productCode.value, next)
    currentStatus.value = next
    notify(`판매상태를 '${MALL_PRODUCT_STATUS_LABEL[next]}'으로 바꿨습니다.`)
  } catch (error) {
    ;(event.target as HTMLSelectElement).value = prev
    notify(error instanceof Error ? error.message : '판매상태를 바꾸지 못했습니다.')
  } finally {
    saving.value = false
  }
}

const problems = computed(() => {
  const list = productProblems(form)
  if (!isApproved.value) {
    list.push('판매업체 승인 심사가 끝나야 등록을 요청할 수 있습니다.')
  }
  return list
})

const firstProblem = computed(() => problems.value[0] ?? '')
const canRequest = computed(() => problems.value.length === 0)

const previewProduct = computed(() =>
  toPreviewProduct(form, {
    code: productCode.value,
    storeName: seller.value?.storeName ?? '',
    storeCutoff: storeShipping.cutoff,
    storeReturnFee: storeShipping.returnFee,
    storeReturnPolicy: storeShipping.returnPolicy,
  }),
)

function onBackdrop() {
  if (props.persistent) return
  void onClose()
}

function onEscape() {
  if (props.persistent) return
  void onClose()
}

async function onClose() {
  if (saving.value) return
  if (dirty.value) {
    const ok = await confirmDialog({
      title: '작성 내용 확인',
      message: '저장하지 않은 상품 변경 내용이 있습니다. 닫을까요?',
      confirmLabel: '닫기',
      tone: 'danger',
    })
    if (!ok) return
  }
  emit('close')
}

async function save(status: MallProductStatus, options: { skipConfirm?: boolean } = {}) {
  if (saving.value || loading.value) return

  /* 서버에서도 강제하지만, UI에서도 판매중 수정은 다음과 같이 저장한다.
     - 마진 변경(판매가·공급가·마진 배분율) 있으면 재검수로 넘어간다.
     - 그 외 수정은 판매 상태를 유지하고 즉시 반영된다(자동 승인).
     서버 유효성은 판매업체가 직접 SELLING 을 저장하지 못하게 SELLER_STATUS(=DRAFT/REQUESTED)만
     허용한다. 자동 승인 케이스에도 API 로는 REQUESTED 를 보내고, 서버가 현재 상태를 유지한다. */
  const wasLiveEdit = needsReReview.value
  const marginChanged = wasLiveEdit && hasMarginChange.value
  const keepListing = wasLiveEdit && !marginChanged
  const nextStatus: MallProductStatus = wasLiveEdit ? 'REQUESTED' : status

  /* 판매·자동승인 저장은 REQUESTED 와 동일한 수준의 필수값(원가·대표 이미지 등)이 필요하다. */
  if ((nextStatus === 'REQUESTED' || keepListing) && !canRequest.value) {
    notify(firstProblem.value)
    onProblemJump()
    return
  }

  if (form.name.trim().length < 2) {
    notify('상품명을 2자 이상 입력해 주세요.')
    onProblemJump()
    return
  }

  if (!options.skipConfirm) {
    const confirmMessage = keepListing
      ? '수정 내용을 바로 반영할까요? 판매 상태는 유지됩니다.'
      : marginChanged
        ? '판매가·공급가·마진이 바뀌었습니다. 관리자 재검수가 필요하고, 검수 전까지 몰에서 내려갑니다. 검수 요청할까요?'
        : nextStatus === 'REQUESTED'
          ? isEdit.value
            ? '수정 내용을 검수 요청할까요?'
            : '상품 등록을 요청할까요?'
          : '임시 저장할까요?'

    const confirmTitle = keepListing
      ? '수정 저장'
      : marginChanged
        ? '재검수 요청'
        : nextStatus === 'REQUESTED'
          ? '검수 요청'
          : '임시 저장'

    const confirmLabel = keepListing || nextStatus !== 'REQUESTED' ? '저장' : '요청'

    const ok = await confirmAction(confirmMessage, {
      title: confirmTitle,
      confirmLabel,
      tone: 'primary',
    })
    if (!ok) return
  }

  saving.value = true
  try {
    const payload = toProductPayload(form, nextStatus)
    const saved = isEdit.value
      ? await updateSellerProduct(productCode.value, payload)
      : await createSellerProduct(payload)

    const current = seller.value
    if (current) {
      try {
        await saveProfile({
          storeName: current.storeName || '',
          bizName: current.bizName || '',
          ownerName: current.ownerName || '',
          managerName: current.managerName || '',
          email: current.email || '',
          phone: current.phone || '',
          zipcode: current.zipcode || '',
          address: current.address || '',
          addressDetail: current.addressDetail || '',
          settleBank: current.settleBank || '',
          settleAccount: current.settleAccount || '',
          settleHolder: current.settleHolder || '',
          shippingFee: current.shippingFee ?? 0,
          freeShipOver: 0,
          returnFee: Math.max(0, Number(storeShipping.returnFee) || 0),
          cutoff: storeShipping.cutoff || '14:00',
          returnPolicy: storeShipping.returnPolicy || DEFAULT_RETURN_POLICY,
          notify: current.notify,
        })
      } catch {
        /* 상품 저장은 성공. 스토어 배송 정책 저장 실패는 별도 안내 */
        notify('상품은 저장됐지만 교환·반품 안내 저장에 실패했습니다. 판매업체 정보에서 다시 저장해 주세요.')
      }
    }

    upsert(saved)
    currentStatus.value = saved.status
    saveRecentProductDefaults(form)
    hasRecentDefaults.value = true
    clearLocalProductDraft()
    localDraftBanner.value = ''
    markFormClean()
    notify(
      keepListing
        ? '수정 내용을 바로 반영했습니다. 몰 노출은 그대로 유지됩니다.'
        : marginChanged
          ? '판매가·공급가·마진이 바뀌어 관리자 재검수로 넘어갑니다. 검수 전까지 몰에서 내려갑니다.'
          : nextStatus === 'REQUESTED'
            ? Boolean(saved.reviewedAt || saved.saleStartAt)
              ? '수정 내용을 다시 검수 요청했습니다. 검수 전까지 몰에서 내려갑니다.'
              : '등록 요청을 보냈습니다. 관리자 검수 후 함우리몰에 올라갑니다.'
            : '임시 저장했습니다.',
    )
    emit('saved', saved.code)
  } catch (error) {
    notify(error instanceof Error ? error.message : '저장에 실패했습니다.')
  } finally {
    saving.value = false
  }
}
</script>

<style lang="scss" src="./SellerProductFormModal.scss"></style>
